import { useEffect, useState } from "react";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { Settings, Add } from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";

import "./avatarScreen.scss";

import { format } from "date-fns";
import { Scan } from "../../types/interfaces";
import { CircularProgress } from "@mui/material";
import SubmissionDiscardModal from "@/Components/Modal/SubmissionDiscardModal";
import { pipelineStatusColors } from "@/Utils/constants";
import { acknowledgeScanSubmitted } from "@/Utils/scanSubmittedMarker";
import { useScansList } from "@/hooks/useScansList";

const AvatarScreen = () => {
  const { navigateTo } = useCustomNavigation();

  // The list comes from the app's react-query cache (useScansList): a return
  // to this screen shows it at once, and only scans still being processed are
  // watched. `docs` is undefined until an answer exists, which the list area
  // must show as "not known yet", never as "No recent avatars".
  const { docs, showSpinner } = useScansList("AvatarScreen");
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  function updateListItems(documents: any[]): Scan[] {
    const scans: Scan[] = [];

    documents.forEach((document) => {
      const {
        pipelineStatus,
        storagePath,
        timestamp,
        name,
        failureReason,
        id,
        userId,
        clientIds,
        failureMessage,
      } = document;

      if (!pipelineStatus || !storagePath || !timestamp) {
        return;
      }

      const timeInterval = new Date(timestamp * 1000);
      const formattedDate = format(timeInterval, "MMM d yyyy, hh:mma");

      let status: string;
      let color: string;

      switch (pipelineStatus) {
        case "PROCESSING":
          status = "Avatar processing";
          color = pipelineStatusColors["PROCESSING"];
          break;
        case "FAILURE":
          status = "Avatar failed";
          color = pipelineStatusColors["FAILURE"];
          break;
        case "UPLOADING":
          status = "Submitting Scans ...";
          color = pipelineStatusColors["APPROVED"];
          break;

        case "APPROVED":
          status = "Avatar ready";
          color = pipelineStatusColors["SUCCESS"];
          break;
        default:
          status = "Avatar processing";
          color = pipelineStatusColors["PROCESSING"];
      }

      const scan: Scan = {
        name: name || "No Name",
        date: formattedDate,
        status: status,
        color: color,
        id: id,
        userId: userId,
        clientIds,
        failureReason,
        failureMessage,
        originalTimestamp: timestamp * 1000,
      };

      scans.push(scan);
    });
    // Sort scans by original timestamp (most recent first)
    scans.sort((a, b) => b.originalTimestamp - a.originalTimestamp);

    return scans;
  }
  const scans = docs ? updateListItems(docs) : [];
  const scansResolved = docs !== undefined;

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hasVisited = sessionStorage.getItem("hasVisited");

    if (!hasVisited) {
      const timer = setTimeout(() => {
        setLoading(false);
        sessionStorage.setItem("hasVisited", "true");
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <>
      {loading || showSpinner ? (
        <div className="loader-container">
          <CircularProgress />
        </div>
      ) : (
        <div
          style={{
            background: "var(--sm-bg, rgba(240, 242, 250, 1))",
          }}
          className="main-grid"
        >
          <SubmissionDiscardModal open={open} handleClose={handleClose} />
          <div className="list-avatar-header-wrap">
            <div className="list-avatar-header-container">
              <div className="avatars">
                <h1>Avatars</h1>
              </div>
              <div
                onClick={() => {
                  navigateTo("/settings");
                }}
                className="settings"
              >
                <Settings className="settings-icon" />
              </div>
            </div>
          </div>
          <div className="list-avatar-main-wrap">
            {!scansResolved ? (
              // No answer yet (the first load passed its 10 s deadline, or
              // failed). The screen is usable; the list is not known.
              <div className="list-avatar-pending">
                <CircularProgress />
              </div>
            ) : scans.length === 0 ? (
              <div className="no-scan-data">
                <div className="avatar-icon">
                  <svg
                    width="115"
                    height="160"
                    viewBox="0 0 115 160"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 124.618C20 111.409 24.3684 106.11 20 97.5035C14.4223 86.5146 0 90.3069 0 79.9999C0 61.9072 24.5 55.7521 24.5 35.6557C24.5 19.6351 20 -18.0002 69.5 11.4998C79.5173 17.4697 101.333 14.2278 104.519 19.6351C109.874 28.7232 115.218 30.9625 111.871 42.6557C108.933 52.9199 85 56.205 85 66.4999C85 76.2186 96.7551 85.2995 104.519 91.4999C117.828 102.128 120 124.618 97.8639 130.18C83.1442 133.879 67.1412 119.053 59 130.18C29.5 170.5 21.5 134 20 124.618Z"
                      fill="#E2E8F5"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M63.1823 43.6899C63.1823 45.8469 62.3628 48.6643 60.9865 50.9197C59.5339 53.3001 58.0002 54.2404 56.936 54.2404C55.8718 54.2404 54.3381 53.3001 52.8855 50.9197C51.5092 48.6643 50.6897 45.8469 50.6897 43.6899C50.6897 39.4719 53.7211 36.5 56.936 36.5C60.1509 36.5 63.1823 39.4719 63.1823 43.6899ZM56.936 56.7404C61.7664 56.7404 65.6823 49.0414 65.6823 43.6899C65.6823 38.3383 61.7664 34 56.936 34C52.1055 34 48.1897 38.3383 48.1897 43.6899C48.1897 49.0414 52.1055 56.7404 56.936 56.7404ZM111.333 64.5942C111.333 63.9039 110.774 63.3442 110.083 63.3442H3.25C2.55964 63.3442 2 63.9039 2 64.5942C2 65.2846 2.55964 65.8442 3.25 65.8442H110.083C110.774 65.8442 111.333 65.2846 111.333 64.5942ZM33.1443 155.318C35.2441 140.71 37.9846 121.51 40.2001 105.962C41.3079 98.1873 42.2844 91.3261 42.984 86.4083L43.8075 80.6183L44.0293 79.058L44.0867 78.6538L44.1013 78.551L44.105 78.5251L44.1059 78.5186L44.1062 78.517C44.1062 78.5166 44.1063 78.5164 42.8687 78.3405C41.6311 78.1647 41.6311 78.1649 41.631 78.1652L41.6308 78.1668L41.6299 78.1734L41.6262 78.1993L41.6116 78.3021L41.5542 78.7062L41.3324 80.2664L40.5089 86.0562C39.8094 90.9738 38.8329 97.8349 37.7251 105.609C35.5096 121.157 32.7693 140.356 30.6698 154.963C30.3571 157.138 31.6608 158.727 33.3316 159.142C34.9945 159.556 36.8962 158.776 37.6771 156.735L53.4372 115.536C53.9854 114.103 55.3607 113.157 56.895 113.157C58.4259 113.157 59.799 114.099 60.3495 115.528L75.8787 155.822C76.6739 157.885 78.6111 158.638 80.279 158.21C81.9525 157.781 83.2801 156.181 82.9484 153.986C80.7519 139.451 78.0067 120.497 75.8103 105.174C74.7122 97.5136 73.7514 90.7614 73.0651 85.9244C72.7219 83.5059 72.4474 81.5663 72.2587 80.2313L72.0421 78.698L71.9861 78.301L71.9719 78.2L71.9683 78.1746L71.9674 78.1682L71.9672 78.1666C71.9671 78.1663 71.9671 78.1661 70.7293 78.3405C69.4916 78.515 69.4916 78.5152 69.4916 78.5155L69.4919 78.5171L69.4928 78.5235L69.4964 78.549L69.5106 78.6501L69.5667 79.0474L69.7833 80.5812C69.9721 81.9166 70.2467 83.8566 70.5899 86.2756C71.2763 91.1135 72.2373 97.8669 73.3356 105.529C75.5321 120.853 78.2785 139.815 80.4764 154.36C80.6063 155.219 80.1558 155.661 79.658 155.788C79.1546 155.918 78.5274 155.743 78.2115 154.923L62.6823 114.629C61.7599 112.235 59.4597 110.657 56.895 110.657C54.3246 110.657 52.0206 112.242 51.1022 114.643L35.3421 155.841C35.0259 156.668 34.4079 156.834 33.9346 156.716C33.4691 156.601 33.0201 156.183 33.1443 155.318Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div className="no-recent-avatars">No recent avatars</div>
                <div className="create">Create a new one below</div>
              </div>
            ) : (
              <div className="recent-scan-data">
                {scans.map((scan: any, index: number) => (
                  <div
                    onClick={() => {
                      if (scan.status === "Submitting Scans ...") {
                        navigateTo("/avatar-generation", {
                          state: { date: scan.date, name: scan.name },
                        });
                      } else if (scan.status === "Avatar processing") {
                        navigateTo("/avatar-processing", {
                          state: {
                            date: scan.date,
                            name: scan.name,
                            id: scan.id,
                          },
                        });
                      } else if (scan.status === "Avatar ready") {
                        navigateTo("/avatar-preview", {
                          state: {
                            date: scan.date,
                            name: scan.name,
                            id: scan.id,
                            clientIds: scan.clientIds,
                          },
                        });
                      } else if (scan.status === "Avatar failed") {
                        navigateTo("/avatar-failed", {
                          state: {
                            failureMessage: scan.failureMessage,
                            date: scan.date,
                            name: scan.name,
                            id: scan.id,
                          },
                        });
                      }
                    }}
                    key={index}
                    className="scan-item"
                  >
                    <div className="scan-details">
                      <div className="title">
                        <div className="name">
                          <PermIdentityIcon />
                          {scan.name}{" "}
                        </div>
                        <div className="arrow-icon">
                          <ArrowForwardIosIcon />
                        </div>
                      </div>

                      <div className="date">{scan.date}</div>

                      <div className="avatar-status">
                        <span
                          className="circle"
                          style={{ backgroundColor: scan.color }}
                        ></span>
                        {scan.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="list-avatar-footer-container">
            <button
              onClick={() => {
                if (localStorage.getItem("scanID") !== null) {
                  setOpen(true);
                  return;
                }
                // Pressing this from the avatars list — where a just-submitted
                // scan is visible as Processing — is the user saying they know
                // the previous scan landed and want another one. Acknowledge
                // the marker so the form they are about to see shows a plain
                // Start button instead of the "Submitted" reassurance, which is
                // only for people who arrived without being told. The marker
                // itself stays live: `handleStartScan` still clears it when the
                // scan actually starts.
                //
                // Unscoped on purpose — this button means "whatever was last
                // submitted", and the list the user is pressing from is showing
                // that scan as Processing. The stand-down is bounded by
                // SCAN_SUBMITTED_ACK_TTL_MS, so a later stray arrival at the
                // form still gets the reassurance.
                acknowledgeScanSubmitted();
                if (
                  typeof (DeviceMotionEvent as any).requestPermission ===
                  "function"
                ) {
                  navigateTo("/new-avatar-screen", {
                    state: {
                      compatible: true,
                    },
                  });
                } else {
                  navigateTo("/new-avatar-screen", {
                    state: {
                      compatible: false,
                    },
                  });
                }
              }}
              className="new-avatar-button"
            >
              <Add className="add-icon" /> New Avatar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AvatarScreen;
